import React, { useState, useEffect, useCallback, useMemo, useRef, Suspense, lazy } from 'react';
import { useLocation } from 'react-router-dom';
import { MapContainer, TileLayer, Polyline, CircleMarker, Marker, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getActiveTrips, getDelivered, ackDelivered, normalizeTrip, statusMeta, fetchTripRoute, reopenTrip } from '../truckmateStore';
import { geocode, isAdmin, isDispatcher, isCompanyAdmin } from '../traccar';
import { playOpen } from '../clickSound';
import GoogleMutantLayer from '../GoogleMutantLayer';
import WatchtowerPanel from './WatchtowerPanel';
import TripSheetsPanel from './TripSheets';
import DispatchWorkspace from './dispatch/DispatchWorkspace';
import { compareByBill, ltlRun, runDayLabel, HUB_ROUTES, matchRun } from './dispatch/model';
import TripSearchPanel from './dispatch/TripSearchPanel';
import { RundownButton } from './dispatch/TripCard';
import { getRundowns, sendRundown, getEmails, getTraining, getRequests } from '../truckmateStore';
import { stopWords, isDown } from './dispatch/model';
import { trackTab, savedTab, rememberTab } from './dispatch/scrollMemory';

// Leaflet-heavy fleet map loads only when its tab is opened.
const FleetMap = lazy(() => import('./dispatch/FleetMap'));
const TextingSetup = lazy(() => import('./dispatch/TextingSetup'));
const JarvisInbox = lazy(() => import('./dispatch/JarvisInbox'));
const OutboundReport = lazy(() => import('./dispatch/OutboundReport'));
const NotClosed = lazy(() => import('./dispatch/NotClosed'));
const FlowerReport = lazy(() => import('./dispatch/FlowerReport'));
const StopTimes = lazy(() => import('./dispatch/StopTimes'));
const JarvisChat = lazy(() => import('./dispatch/JarvisChat'));
const CustomerProfiles = lazy(() => import('./dispatch/CustomerProfiles'));
const Geofences = lazy(() => import('./dispatch/Geofences'));
const CallsTexts = lazy(() => import('./dispatch/CallsTexts'));
const Employees = lazy(() => import('./dispatch/Employees'));
const DriverProfiles = lazy(() => import('./dispatch/DriverProfiles'));
const MissingBoxes = lazy(() => import('./dispatch/MissingBoxes'));
const RoadCalls = lazy(() => import('./dispatch/RoadCalls'));
const DispatchersAdmin = lazy(() => import('./dispatch/DispatchersAdmin'));
const JarvisBubble = lazy(() => import('./dispatch/JarvisBubble'));
const TeachJarvis = lazy(() => import('./dispatch/TeachJarvis'));
const CompaniesAdmin = lazy(() => import('./dispatch/CompaniesAdmin'));
const PushRulesAdmin = lazy(() => import('./dispatch/PushRulesAdmin'));

// ---- TagAlong-style live tracking map --------------------------------------
// This mirrors the TagAlong app's Live Tracking exactly: Google satellite/road
// tiles (with an ESRI fallback), a classic GPS triangle arrow that points where
// the truck is heading and is colored by ignition (green = engine on, red = off),
// a soft pulsing halo, and the same glanceable stats card under the arrow.
const ESRI_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const ignitionColor = (ign) => (ign ? '#22c55e' : '#ef4444');

// inject the marker pulse keyframes once (same animation TagAlong uses)
let _tmPulseInjected = false;
function ensurePulseCss() {
  if (_tmPulseInjected || typeof document === 'undefined') return;
  _tmPulseInjected = true;
  const s = document.createElement('style');
  s.textContent = `
    .tm-marker-pulse{position:absolute;top:50%;left:50%;width:26px;height:26px;margin:-13px 0 0 -13px;border-radius:50%;opacity:.5;animation:tmMarkerPulse 2s ease-out infinite;}
    @keyframes tmMarkerPulse{0%{transform:scale(.6);opacity:.55;}100%{transform:scale(2.4);opacity:0;}}
    @keyframes tmToastIn{0%{transform:translateX(24px);opacity:0;}100%{transform:translateX(0);opacity:1;}}`;
  document.head.appendChild(s);
}

// bearing between two [lat,lng] points, so the arrow points along the road when
// the tracker doesn't send a heading.
const tmBearing = (a, b) => {
  const r = Math.PI / 180;
  const y = Math.sin((b[1] - a[1]) * r) * Math.cos(b[0] * r);
  const x = Math.cos(a[0] * r) * Math.sin(b[0] * r) - Math.sin(a[0] * r) * Math.cos(b[0] * r) * Math.cos((b[1] - a[1]) * r);
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
};

// Does this trip's live feed come from the FMC00A (Traccar OBD) or only from
// Samsara? Both now report the same readings (battery, engine, fuel, speed,
// heading, fault codes); this only decides the "via ..." label and the
// FMC00A-only signal chips (rssi / sats).
const hasObdFeed = (l) => !!(l && (l.sat != null || l.rssi != null || (l.source && String(l.source).includes('traccar'))));
// engine considered "on" if the OBD ignition says so, or Samsara's engine state
// reads on/idle/running, or the truck is simply moving.
const engineIsOn = (l) => !!(l && (l.ignition === true || (l.engine && /on|idl|run|driv/i.test(String(l.engine))) || (l.speedMph || 0) > 0));

// The classic GPS arrow + rich glanceable card — same visual language as the
// TagAlong app's SmoothVehicle marker. Adapts its chips to the live source.
const truckArrowIcon = (live, unit, driverName, course, etaText, tempUnits) => {
  const l = live || {};
  const obd = hasObdFeed(l);
  const on = engineIsOn(l);
  const col = ignitionColor(on);
  const speedMph = Math.round(l.speedMph || 0);
  const chip = (bg, fg, txt) => `<span style="background:${bg};color:${fg};border-radius:5px;padding:2px 6px;font-weight:700;border:1px solid rgba(255,255,255,0.18);text-shadow:0 1px 2px rgba(0,0,0,0.5)">${txt}</span>`;
  // One layout for every truck — each chip shows when its device reports it.
  const power = l.power != null ? Number(l.power) : null;
  const engTxt = /idl/i.test(String(l.engine || '')) ? 'IDLE' : (on ? 'ON' : 'OFF');
  const showTemp = l.tempF != null && !l.tempStale;
  const rows = `
      <div style="display:flex;gap:4px;align-items:center">
        ${chip('rgba(255,255,255,0.14)', '#fff', `⚡ ${power != null ? power.toFixed(1) + 'V' : '—'}`)}
        ${chip(col, '#fff', `🔑 ${engTxt}`)}
        ${chip('#475569', '#fff', obd ? '🔌 Wired' : '📡 ELD')}
      </div>
      <div style="display:flex;gap:4px;align-items:center;margin-top:2px">
        ${chip('rgba(255,255,255,0.14)', '#fff', `${speedMph} mph`)}
        ${l.fuelPct != null ? chip('rgba(255,255,255,0.14)', '#fff', `⛽ ${l.fuelPct}%`) : ''}
        ${l.dtcCodes && l.dtcCodes.length ? chip('#dc2626', '#fff', `🔧 ${l.dtcCodes.length}`) : ''}
        ${l.rssi != null ? chip('#16a34a', '#fff', `📶 ${l.rssi}`) : ''}
        ${l.sat != null ? chip('#eab308', '#fff', `🛰️ ${l.sat}`) : ''}
      </div>
      ${showTemp ? `<div style="display:flex;gap:4px;align-items:center;margin-top:2px">${chip('#0ea5e9', '#fff', `❄️ ${l.tempF}°${tempUnits || 'F'}${l.setpointF != null ? ` / set ${l.setpointF}°` : ''}`)}</div>` : ''}`;
  const src = obd ? 'FMC00A' : 'Samsara';
  return L.divIcon({
    className: '', iconSize: [30, 30], iconAnchor: [15, 15],
    html: `
      <div style="position:relative">
        <span class="tm-marker-pulse" style="background:${col}"></span>
        <svg width="30" height="30" viewBox="0 0 40 40" style="position:relative;transform:rotate(${course || 0}deg);filter:drop-shadow(0 2px 3px rgba(0,0,0,.6))">
          <polygon points="20,3 33,35 20,27 7,35" fill="${col}" stroke="white" stroke-width="2.5" stroke-linejoin="round"/>
        </svg>
        <div style="position:absolute;top:34px;left:50%;transform:translateX(-50%);background:rgba(9,13,22,0.34);-webkit-backdrop-filter:blur(14px) saturate(150%);backdrop-filter:blur(14px) saturate(150%);border:1px solid rgba(255,255,255,0.22);border-radius:12px;padding:6px 9px;white-space:nowrap;box-shadow:0 6px 18px rgba(0,0,0,.35),inset 0 1px 0 rgba(255,255,255,0.14);font-size:11px;line-height:1.55;color:#fff;text-shadow:0 1px 3px rgba(0,0,0,0.85)">
          <div style="font-weight:800;font-size:12px;margin-bottom:3px;text-shadow:0 1px 4px rgba(0,0,0,0.9)">🚚 ${unit ? `Truck ${unit}` : 'Truck'}${driverName ? ` · ${driverName}` : ''}</div>
          ${rows}
          <div style="margin-top:3px;font-size:10px;color:#7dd3fc">via ${src}${etaText ? ` · ETA ${etaText}` : ''}</div>
        </div>
      </div>`,
  });
};

// ---- trip breadcrumb map ---------------------------------------------------
// Fits the map to the whole route (+ current position) whenever the points load.
function FitBounds({ coords }) {
  const map = useMap();
  useEffect(() => {
    if (!coords || !coords.length) return;
    if (coords.length === 1) { map.setView(coords[0], 15); return; }
    map.fitBounds(L.latLngBounds(coords), { padding: [26, 26], maxZoom: 15 });
  }, [coords, map]);
  return null;
}

// Redraws the map when its container gets a size (Google mutant needs this or it
// tiles into a 0×0 box and shows grey).
function TripAutoResize() {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 200);
    if (!('ResizeObserver' in window)) return () => clearTimeout(t);
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(map.getContainer());
    return () => { clearTimeout(t); ro.disconnect(); };
  }, [map]);
  return null;
}

// Draws where the truck has been over the trip window + a live truck marker,
// styled exactly like the TagAlong app.
function TripMap({ unit, live, sinceIso, destCity, driverName, etaText, tempUnits, height = 300 }) {
  const [pts, setPts] = useState(null);
  const [err, setErr] = useState('');
  const [dest, setDest] = useState(null);
  // same layer choices as TagAlong: Satellite HD (Google) / Map (Google road) /
  // Standard (ESRI). Remembered per browser.
  const [layer, setLayerState] = useState(() => (typeof localStorage !== 'undefined' && localStorage.getItem('tm-map-layer')) || 'gsat');
  const setLayer = (k) => { setLayerState(k); try { localStorage.setItem('tm-map-layer', k); } catch { /* ignore */ } };
  useEffect(() => { ensurePulseCss(); }, []);

  useEffect(() => {
    let cancel = false;
    setPts(null); setErr('');
    (async () => {
      try {
        const r = await fetchTripRoute(unit, sinceIso ? { since: sinceIso } : { hours: 24 });
        if (cancel) return;
        setPts(r.points || []);
        if (!(r.points || []).length && r.reason) setErr(r.reason === 'no-vehicle' ? 'No truck matched this unit.' : 'No live GPS source.');
      } catch (e) { if (!cancel) { setPts([]); setErr(e.message || 'Could not load route.'); } }
    })();
    return () => { cancel = true; };
  }, [unit, sinceIso]);

  // geocode the destination city for a red pin
  useEffect(() => {
    let cancel = false;
    (async () => {
      if (!destCity) { setDest(null); return; }
      const zip = zipOf(destCity);
      let coord = zip ? geoGet(zip) : undefined;
      if (coord === undefined) { coord = await geocodeStop(destCity); if (zip) geoSet(zip, coord); }
      if (!cancel && coord) setDest([coord.lat, coord.lng]);
    })();
    return () => { cancel = true; };
  }, [destCity]);

  const line = (pts || []).map((p) => [p.lat, p.lng]);
  const cur = (live && live.lat != null && live.lng != null) ? [live.lat, live.lng] : (line.length ? line[line.length - 1] : null);
  const coords = [...line, ...(cur ? [cur] : []), ...(dest ? [dest] : [])];
  // arrow heading: tracker course if we have it, else bearing of the last hop.
  const course = (live && live.course != null)
    ? live.course
    : (line.length >= 2 ? tmBearing(line[line.length - 2], line[line.length - 1]) : 0);

  if (pts === null) return <div style={{ fontSize: '12px', color: INK2, padding: '10px 2px' }}>Loading route…</div>;
  if (!line.length && !cur) return <div style={{ fontSize: '12px', color: INK3, padding: '10px 2px' }}>📡 {err || 'No GPS history for this truck yet.'}</div>;

  const layers = [['gsat', '🛰️ Satellite HD'], ['groad', '🗺️ Map'], ['esri', '🌍 Standard']];

  return (
    <div style={{ marginTop: '8px' }}>
      <div style={{ display: 'flex', gap: '6px', marginBottom: '8px', flexWrap: 'wrap' }}>
        {layers.map(([k, label]) => (
          <button key={k} onClick={() => setLayer(k)}
            style={{ padding: '4px 11px', borderRadius: '7px', fontSize: '11px', fontWeight: 700, cursor: 'pointer',
              border: layer === k ? `1px solid ${NEON}` : `1px solid ${LINE}`,
              background: layer === k ? NEON : PANEL, color: layer === k ? '#fff' : INK2 }}>
            {label}
          </button>
        ))}
      </div>
      <div style={{ height: `${height}px`, borderRadius: '12px', overflow: 'hidden', border: `1px solid ${LINE}` }}>
        <MapContainer center={cur || line[0]} zoom={15} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
          {(layer === 'gsat' || layer === 'groad') && (
            <>
              <TileLayer url={ESRI_URL} attribution="Tiles &copy; Esri" />
              <GoogleMutantLayer type={layer === 'groad' ? 'roadmap' : 'hybrid'} />
            </>
          )}
          {layer === 'esri' && (
            <>
              <TileLayer url={ESRI_URL} attribution="Tiles &copy; Esri" />
              <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}" />
              <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}" />
            </>
          )}
          {line.length > 1 && <Polyline positions={line} pathOptions={{ color: '#0066ff', weight: 5, opacity: 0.9 }} />}
          {line.length > 0 && (
            <CircleMarker center={line[0]} radius={6} pathOptions={{ color: '#ffffff', weight: 2, fillColor: GREEN, fillOpacity: 1 }}>
              <Tooltip>Start</Tooltip>
            </CircleMarker>
          )}
          {dest && (
            <CircleMarker center={dest} radius={6} pathOptions={{ color: '#ffffff', weight: 2, fillColor: RED, fillOpacity: 1 }}>
              <Tooltip>Destination{destCity ? ` · ${destCity}` : ''}</Tooltip>
            </CircleMarker>
          )}
          {cur && (
            <Marker position={cur} icon={truckArrowIcon(live, unit, driverName, course, etaText, tempUnits)} zIndexOffset={1000} />
          )}
          <TripAutoResize />
          <FitBounds coords={coords} />
        </MapContainer>
      </div>
      <div style={{ fontSize: '10px', color: INK3, marginTop: '6px' }}>
        {line.length ? `${line.length} GPS points` : 'Live position only'} · Live from {hasObdFeed(live) ? 'FMC00A' : 'Samsara'}{err ? ` · ${err}` : ''} · 🟢 origin · ➤ truck · 🔴 destination
      </div>
    </div>
  );
}

// ---- miles-to-customer: geocode a stop's ZIP once, cache it, and measure the
// straight-line distance from the truck's live GPS (×1.2 as a rough road factor).
const GEO_KEY = 'tm-zip-geo';
let _geo = null;
const geoLoad = () => { if (_geo) return _geo; try { _geo = JSON.parse(localStorage.getItem(GEO_KEY) || '{}'); } catch { _geo = {}; } return _geo; };
const geoGet = (zip) => { const c = geoLoad(); return (zip in c) ? c[zip] : undefined; };
const geoSet = (zip, v) => { const c = geoLoad(); c[zip] = v; try { localStorage.setItem(GEO_KEY, JSON.stringify(c)); } catch { /* ignore */ } };
const zipOf = (s) => { const m = String(s || '').match(/\b(\d{5})\b/); return m ? m[1] : ''; };
async function geocodeStop(cityStr) {
  const parts = String(cityStr || '').split(',').map((x) => x.trim());
  const zip = zipOf(cityStr);
  const city = parts[0] || '';
  const state = (parts[1] || '').match(/^[A-Za-z]{2}$/) ? parts[1] : '';
  try { const r = await geocode({ city, state, zip }); return (r && r.lat != null) ? { lat: r.lat, lng: r.lng } : null; } catch { return null; }
}

// ---- light operations-center theme ----------------------------------------
const PANEL = '#FFFFFF', LINE = '#E2E8F0';
const INK = '#172033', INK2 = '#64748B', INK3 = '#94A3B8';
const NEON = '#1677FF', GREEN = '#10B981', RED = '#EF4444';
const TONE = {
  go: { fg: '#1677FF', bg: 'rgba(22,119,255,.12)' },
  wait: { fg: '#B45309', bg: 'rgba(245,158,11,.16)' },
  done: { fg: '#047857', bg: 'rgba(16,185,129,.14)' },
  idle: { fg: '#64748B', bg: 'rgba(100,116,139,.12)' },
  bad: { fg: '#B91C1C', bg: 'rgba(239,68,68,.14)' },
};
const timeAgo = (mins) => {
  if (mins == null) return '';
  if (mins <= 0) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  return h < 24 ? `${h}h ${mins % 60}m ago` : `${Math.floor(h / 24)}d ago`;
};
const dt = (s) => (s ? new Date(s).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

// Bill-number prefix → friendly label (broker / LTL / origin terminal). Fill in
// per Florida Beauty's scheme; any unmapped letter just shows as "<letter> loads".
const BILL_LABELS = {
  // e.g.  B: 'Broker',  M: 'Miami LTL',  C: 'Memphis',  H: 'Houston', …
};
const billLabel = (p) => (BILL_LABELS[p] ? `${BILL_LABELS[p]} (${p})` : `${p} loads`);

const card = { background: PANEL, border: `1px solid ${LINE}`, borderRadius: '14px', boxShadow: '0 1px 2px rgba(15,23,42,.05), 0 12px 28px -20px rgba(15,23,42,.28)' };

function Pill({ tone, children, wrap }) {
  const c = TONE[tone] || TONE.idle;
  return (
    <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: '999px', fontSize: '11px', fontWeight: 800, color: c.fg, background: c.bg, whiteSpace: wrap ? 'normal' : 'nowrap', lineHeight: 1.3, textAlign: 'center' }}>
      {children}
    </span>
  );
}

// ---- one manifest stop (a consignee delivery), numbered like the TM manifest
// ---- delivered queue row --------------------------------------------------
const RUNDOWN_TONE = { emailed: 'done' };
function DeliveredRow({ item, onAck, busy, rundown, onResend, onReopen }) {
  const [sending, setSending] = useState(false);
  return (
    <div style={{ ...card, marginBottom: '8px', padding: '11px 14px', display: 'flex', alignItems: 'center', gap: '12px', opacity: item.acknowledged ? 0.6 : 1 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 800, fontSize: '14px', color: INK }}>#{item.tripNumber}</span>
          <Pill tone="done">Delivered</Pill>
          {item.acknowledged && <Pill tone="idle">Handled</Pill>}
          {rundown && <Pill tone={RUNDOWN_TONE[rundown.status] || 'idle'}>Rundown: {rundown.status}{rundown.sentTo ? ` → ${rundown.sentTo.join(', ')}` : ''}</Pill>}
        </div>
        {rundown && rundown.error && <div style={{ fontSize: '11px', color: '#b91c1c', marginTop: '2px' }}>{rundown.error}</div>}
        <div style={{ fontSize: '12px', color: INK2, marginTop: '3px' }}>
          {item.destinationZone || '—'}{item.driver ? ` · Driver ${item.driver}` : ''}
        </div>
        <div style={{ fontSize: '11px', color: INK3, marginTop: '2px' }}>
          {(item.billNumbers || []).length ? `Bills: ${item.billNumbers.join(', ')} · ` : ''}{dt(item.deliveredAt)}
        </div>
        {item.reason && <div style={{ fontSize: '11.5px', color: INK2, marginTop: '2px' }}>Finished: {item.reason}</div>}
      </div>
      <RundownButton trip={item.tripNumber} />
      {item.reason && onReopen && <button type="button" className="tmw-btn tmw-btn-sm" onClick={() => { if (window.confirm(`Reopen trip ${item.tripNumber}? It goes back to Active trips.`)) onReopen(item.tripNumber); }}>Reopen</button>}
      <button type="button" className="tmw-btn tmw-btn-sm" disabled={sending}
        onClick={async () => { setSending(true); try { await onResend(item.tripNumber); } finally { setSending(false); } }}>{sending ? 'Sending…' : 'Email rundown'}</button>
      {!item.acknowledged && (
        <button
          onClick={() => onAck(item.tripNumber)}
          disabled={busy}
          style={{ padding: '7px 12px', borderRadius: '8px', border: 'none', background: busy ? INK3 : GREEN, color: '#04241a', fontSize: '12px', fontWeight: 800, cursor: busy ? 'default' : 'pointer', whiteSpace: 'nowrap' }}
        >
          Mark handled
        </button>
      )}
    </div>
  );
}

// ---- page ------------------------------------------------------------------
export default function TruckMatePage() {
  const [status, setStatus] = useState(null);   // ingest/latest payload
  const [report, setReport] = useState('flowers');   // which report on the Reports tab
  const [customerQuery, setCustomerQuery] = useState('');   // Fleet map → View customer opens Customers & brokers searched for them
  const [people, setPeople] = useState(false);       // admins: manage dispatcher accounts
  const [companies, setCompanies] = useState(false); // platform admin: AI-dispatcher customer companies
  const [pushes, setPushes] = useState(false);       // admins: who gets which push notifications
  const [delivered, setDelivered] = useState({ items: [], count: 0 });
  const [texting, setTexting] = useState(false);   // RingCentral setup dialog
  const [tab, setTab] = useState(() => { try { return new URLSearchParams(window.location.search).get('tab') || savedTab() || 'watch'; } catch { return 'watch'; } }); // ?tab=calls from a push; else the tab you were on       // 'watch' | 'active' | 'delivered'
  // each tab comes back where you left it (PDFs, other tabs / apps, reloads)
  useEffect(() => { rememberTab(tab); return trackTab(tab); }, [tab]);
  const tabsRef = useRef(null);
  // on phones the tab bar stays pinned: truck headers stick just below it, not behind it
  useEffect(() => {
    const set = () => { const el = tabsRef.current; if (el && el.parentElement) el.parentElement.style.setProperty('--tm-tabs-h', `${el.offsetHeight}px`); };
    set();
    window.addEventListener('resize', set);
    return () => window.removeEventListener('resize', set);
  }, []);
  const [training, setTraining] = useState(null);   // 🧪 training mode banner
  const [canTeach, setCanTeach] = useState(false);  // the server says who gets Teach Jarvis
  useEffect(() => { getRequests().then(() => setCanTeach(true)).catch(() => {}); }, []);
  useEffect(() => { const f = () => getTraining().then(setTraining).catch(() => {}); f(); const t = setInterval(f, 300000); return () => clearInterval(t); }, []);
  // tapping a push (e.g. "Call back needed" → ?tab=inbox) opens that tab
  const location = useLocation();
  useEffect(() => { const t = new URLSearchParams(location.search).get('tab'); if (t) setTab(t); }, [location.search]);
  // phones: the tab strip scrolls sideways — keep the open tab in view
  useEffect(() => { const el = tabsRef.current && tabsRef.current.querySelector('[aria-pressed="true"]'); if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest', inline: 'center' }); }, [tab]);
  const [watchCounts, setWatchCounts] = useState(null); // Priority 1 counts for the tab badge
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [billType, setBillType] = useState('all'); // bill-number prefix filter
  const [ocOnly, setOcOnly] = useState(false);       // show only outside-carrier loads
  const [downOnly, setDownOnly] = useState(false);   // show only broken-down trucks
  // ARTIS-style search for the LTL runs: hub (Miami / Ventura), the night (YYYY-MM-DD), the route (NE, MWE, … NEC, SEC, MWC)
  const [runHub, setRunHub] = useState('all');
  const [runDay, setRunDay] = useState('');
  const [runRoute, setRunRoute] = useState('all');

  const [rundowns, setRundowns] = useState({});      // trip → rundown status (PDF made / emailed)
  const loadRundowns = useCallback(() => { getRundowns().then((l) => setRundowns(Object.fromEntries((l || []).map((r) => [String(r.trip), r])))).catch(() => {}); }, []);
  useEffect(() => { if (tab === 'delivered') loadRundowns(); }, [tab, loadRundowns]);
  const [inboxCount, setInboxCount] = useState(0);   // Jarvis emails not on a load yet
  const loadInboxCount = useCallback(() => { getEmails({ unmatched: true }).then((l) => setInboxCount((l || []).length)).catch(() => {}); }, []);
  useEffect(() => { loadInboxCount(); const t = setInterval(loadInboxCount, 2 * 60000); return () => clearInterval(t); }, [loadInboxCount]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [ackBusy, setAckBusy] = useState('');
  const [showHandled, setShowHandled] = useState(false);
  const [toasts, setToasts] = useState([]); // truck-assigned pop-ups
  // Dispatch workspace state lives HERE so the 60s refresh and tab switches
  // never reset the selected trip or the sections a dispatcher opened.
  const [selectedTrip, setSelectedTrip] = useState(() => { try { return sessionStorage.getItem('tmw-selected') || null; } catch { return null; } });
  const [openMap, setOpenMap] = useState(() => { try { return JSON.parse(sessionStorage.getItem('tmw-open') || '{}'); } catch { return {}; } });
  const [briefReq, setBriefReq] = useState(0);
  const isOpen = useCallback((k) => !!openMap[k], [openMap]);
  const setOpen = useCallback((k, v) => setOpenMap((m) => ({ ...m, [k]: v })), []);
  useEffect(() => { try { sessionStorage.setItem('tmw-open', JSON.stringify(openMap)); } catch { /* ignore */ } }, [openMap]);
  // selection changes ONLY when a dispatcher picks a truck (or closes the panel)
  useEffect(() => { try { if (selectedTrip) sessionStorage.setItem('tmw-selected', selectedTrip); else sessionStorage.removeItem('tmw-selected'); } catch { /* ignore */ } }, [selectedTrip]);

  const dismissToast = useCallback((id) => setToasts((cur) => cur.filter((x) => x._k !== id)), []);

  const load = useCallback(async () => {
    setErr('');
    try {
      const [s, d] = await Promise.all([getActiveTrips(), getDelivered().catch(() => ({ items: [], count: 0 }))]);
      setStatus(s);
      setDelivered(d || { items: [], count: 0 });
      // Pop up any NEW truck→load assignments. Each id is shown once per browser;
      // the first time this device ever loads, seed the acked set so we don't pop
      // for assignments that already happened.
      try {
        const events = (s && s.assignEvents) || [];
        const ACK = 'tm-assign-acked';
        let acked;
        try { acked = new Set(JSON.parse(localStorage.getItem(ACK) || '[]')); } catch { acked = new Set(); }
        if (localStorage.getItem('tm-assign-init') !== '1') {
          events.forEach((e) => acked.add(e.id));
          localStorage.setItem('tm-assign-init', '1');
          localStorage.setItem(ACK, JSON.stringify([...acked].slice(-200)));
        } else {
          const fresh = events.filter((e) => !acked.has(e.id));
          if (fresh.length) {
            fresh.forEach((e) => acked.add(e.id));
            localStorage.setItem(ACK, JSON.stringify([...acked].slice(-200)));
            setToasts((cur) => [...fresh.map((e) => ({ ...e, _k: e.id })), ...cur].slice(0, 4));
            try { playOpen(); } catch { /* ignore */ }
            fresh.forEach((e) => setTimeout(() => dismissToast(e.id), 16000));
          }
        }
      } catch { /* toast is best-effort */ }
    } catch (e) {
      setErr(e.message || 'Could not reach the TruckMate feed.');
    } finally {
      setLoading(false);
    }
  }, [dismissToast]);

  useEffect(() => { ensurePulseCss(); }, []); // toast + marker animations
  useEffect(() => {
    load();
    const id = setInterval(load, 60000); // auto-refresh every minute
    return () => clearInterval(id);
  }, [load]);

  const trips = useMemo(() => {
    // /truckmate/active returns trips at the top level; tolerate the older
    // ingest/latest shape (data.trips) too.
    const raw = (status && (status.trips || (status.data && status.data.trips))) || [];
    return raw.map(normalizeTrip);
  }, [status]);

  const statusOptions = useMemo(() => {
    const seen = new Map();
    trips.forEach((t) => { const m = statusMeta(t.statusCode, t.statusDesc); if (!seen.has(m.label)) seen.set(m.label, 0); seen.set(m.label, seen.get(m.label) + 1); });
    return [...seen.entries()];
  }, [trips]);

  const alertCount = useMemo(() => trips.reduce((s, t) => s + ((t.alerts && t.alerts.length) ? 1 : 0), 0), [trips]);

  // bill-number prefix (B####, C####, M#### …) groups the loads by type/terminal.
  const billPrefixes = (t) => [...new Set((t.bills || []).map((b) => { const m = String(b.billNumber || '').match(/^([A-Za-z])/); return m ? m[1].toUpperCase() : null; }).filter(Boolean))];
  const billTypeOptions = useMemo(() => {
    const seen = new Map();
    trips.forEach((t) => billPrefixes(t).forEach((p) => seen.set(p, (seen.get(p) || 0) + 1)));
    return [...seen.entries()].sort((a, b) => b[1] - a[1]);
  }, [trips]);

  // which LTL run each trip is (hub + night it left) — for the "Left" filter
  const runs = useMemo(() => Object.fromEntries(trips.map((t) => [t.tripNumber, ltlRun(t)])), [trips]);
  const runOn = runHub !== 'all' || !!runDay || runRoute !== 'all';
  // routes to pick: the hub's ARTIS routes + any on the board, with how many trucks match the hub / night
  const routeOptions = useMemo(() => {
    const base = runHub === 'all' ? [...HUB_ROUTES.Miami, ...HUB_ROUTES.Ventura] : (HUB_ROUTES[runHub] || []);
    const seen = new Map(base.map((x) => [x, 0]));
    trips.forEach((t) => { const r = runs[t.tripNumber]; if (r && r.route && matchRun(r, { hub: runHub, day: runDay })) seen.set(r.route, (seen.get(r.route) || 0) + 1); });
    return [...seen.entries()];
  }, [trips, runs, runHub, runDay]);
  const recentDays = useMemo(() => [...new Set(trips.map((t) => runs[t.tripNumber]).filter((r) => r && r.day && (runHub === 'all' || r.hub === runHub)).map((r) => r.day))].sort().reverse().slice(0, 4), [trips, runs, runHub]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return trips.filter((t) => {
      if ((runHub !== 'all' || runDay || runRoute !== 'all') && !matchRun(runs[t.tripNumber], { hub: runHub, day: runDay, route: runRoute })) return false;
      if (statusFilter !== 'all' && statusMeta(t.statusCode, t.statusDesc).label !== statusFilter) return false;
      if (billType !== 'all' && !(t.bills || []).some((b) => String(b.billNumber || '').toUpperCase().startsWith(billType))) return false;
      if (ocOnly && !t.oc) return false;
      if (downOnly && !isDown(t)) return false;
      // a next load still waiting behind the truck's current run shows inside that trip's card — only by its own number
      if (t.queuedBehind && q.replace(/[^0-9]/g, '') !== String(t.tripNumber)) return false;
      if (!q) return true;
      const L = t.live || {};
      const rc = t.rateCon || {};
      // broker load # / PO / reference numbers from the rate con count too
      const hay = `${t.tripNumber} ${t.driver} ${t.driver2} ${L.driver1 || ''} ${L.driver2 || ''} ${L.samsaraDriver || ''} ${t.powerUnit} ${t.trailer} ${t.destination} ${t.origin} ${t.bills.map((b) => `${b.billNumber} ${b.billTo}`).join(' ')} ${rc.loadNumber || ''} ${(rc.referenceNumbers || []).join(' ')} ${rc.broker || ''} ${stopWords(t)}`.toLowerCase();
      const compact = (x) => x.replace(/[^a-z0-9]/g, '');
      if (hay.includes(q) || (compact(q).length >= 5 && compact(hay).includes(compact(q)))) return true;
      // every word somewhere, any order, partial words too ("designer choice" → DESIGNERS CHOICE)
      const words = q.split(/\s+/).filter(Boolean);
      const flat = compact(hay);
      return words.length > 1 && words.every((w) => hay.includes(w) || (compact(w).length >= 3 && flat.includes(compact(w))));
    }).sort((a, b) => compareByBill(a, b, billType));   // ordered by bill number
  }, [trips, query, statusFilter, billType, ocOnly, downOnly, runs, runHub, runDay, runRoute]);

  const doAck = async (tripNumber) => {
    setAckBusy(String(tripNumber));
    try {
      await ackDelivered(tripNumber);
      setDelivered((prev) => ({ ...prev, items: (prev.items || []).map((x) => (String(x.tripNumber) === String(tripNumber) ? { ...x, acknowledged: true } : x)) }));
    } catch (e) { setErr(e.message || 'Could not update.'); }
    finally { setAckBusy(''); }
  };

  const age = status ? status.ageMinutes : null;
  const live = status && status.receivedAt && age != null && age <= 15;
  const errCount = (status && status.errors && status.errors.length) || 0;
  const unackedCount = (delivered.items || []).filter((x) => !x.acknowledged).length;
  const visibleDelivered = (delivered.items || []).filter((x) => showHandled || !x.acknowledged);

  const conflictTrips = trips.filter((t) => (t.alerts && t.alerts.length) || (t.manifest && t.manifest.diffs && t.manifest.diffs.length)).length;
  const renderMap = useCallback((trip) => {
    if (!trip.powerUnit) return <div className="tmw-note">No truck unit on this trip to track.</div>;
    const picks = (trip.bills || []).map((b) => b.actualPickup).filter(Boolean).sort();
    const since = picks.length && !Number.isNaN(Date.parse(picks[0])) ? new Date(picks[0]).toISOString() : undefined;
    return <TripMap unit={trip.powerUnit} live={trip.live} sinceIso={since} destCity={trip.destination} driverName={trip.live && trip.live.driver1 ? trip.live.driver1 : trip.driver} etaText="" tempUnits={trip.tempUnits} height={360} />;
  }, []);
  const toTrip = (trip) => { setQuery(String(trip)); setStatusFilter('all'); setBillType('all'); setSelectedTrip(String(trip)); setTab('active'); };
  // a link from an alert email (?trip=626045) opens on that load
  useEffect(() => { const t = new URLSearchParams(location.search).get('trip'); if (/^\d{5,8}$/.test(t || '')) toTrip(t); }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="truckmate-workspace">
    {/* truck-assigned pop-ups */}
    {toasts.length > 0 && (
      <div style={{ position: 'fixed', top: '76px', right: '20px', zIndex: 5000, display: 'flex', flexDirection: 'column', gap: '10px', width: '340px', maxWidth: 'calc(100vw - 40px)' }}>
        {toasts.map((t) => (
          <div key={t._k} role="status" style={{ background: '#fff', border: '1px solid #cbd3df', borderLeft: '4px solid #6683af', borderRadius: '10px', padding: '12px 14px', boxShadow: '0 14px 34px rgba(15,23,42,.18)', animation: 'tmToastIn .25s ease-out' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
              <div style={{ fontSize: '13.5px', fontWeight: 600 }}>Truck {t.unit} assigned to a load</div>
              <button type="button" aria-label="Dismiss" onClick={() => dismissToast(t._k)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '14px', lineHeight: 1, padding: '0 2px' }}>✕</button>
            </div>
            <div style={{ fontSize: '12.5px', marginTop: '4px' }}>Trip {t.trip}{(t.origin || t.dest) ? ` · ${t.origin || '—'} → ${t.dest || '—'}` : ''}</div>
            {(t.driver || t.trailer) && <div style={{ fontSize: '12px', marginTop: '2px' }}>{t.driver ? `Driver ${t.driver}` : ''}{t.driver && t.trailer ? ' · ' : ''}{t.trailer ? `Trailer ${t.trailer}` : ''}</div>}
          </div>
        ))}
      </div>
    )}

    <header className="tmw-head">
      <div>
        <h1 className="tmw-h1">Dispatch</h1>
        <div className="tmw-stats">
          <span>{err ? 'TruckMate feed unreachable' : status && status.receivedAt ? `${live ? 'Live' : 'Stale'} · TruckMate updated ${timeAgo(age)}${errCount ? ` · ${errCount} connector error${errCount === 1 ? '' : 's'}` : ''}` : (loading ? 'Connecting…' : 'No TruckMate delivery yet')}</span>
          <span><b>{status ? (status.count ?? trips.length) : '—'}</b> active trips</span>
          <span><b>{watchCounts ? watchCounts.critical : '—'}</b> critical</span>
          <span><b>{conflictTrips}</b> {conflictTrips === 1 ? 'trip' : 'trips'} with source conflicts</span>
        </div>
      </div>
      <div className="tmw-head-actions">
        {isAdmin() && <button type="button" className="tmw-btn" onClick={() => setCompanies(true)}>Companies</button>}
        {(isAdmin() || isCompanyAdmin()) && <button type="button" className="tmw-btn" onClick={() => setPeople(true)}>Dispatchers</button>}
        {(isAdmin() || isCompanyAdmin()) && <button type="button" className="tmw-btn" onClick={() => setPushes(true)}>🔔 Push notifications</button>}
        {!isDispatcher() && <button type="button" className="tmw-btn" onClick={() => setTexting(true)}>Calls, texts &amp; email</button>}
        <button type="button" className="tmw-btn" onClick={() => { playOpen(); load(); }}>↻ Refresh</button>
        <button type="button" className="tmw-btn tmw-btn-primary" onClick={() => { setTab('active'); setBriefReq((n) => n + 1); }}>Shift brief</button>
      </div>
    </header>

    {training && training.on && training.show && (
      <div role="status" style={{ background: '#fff7cc', borderTop: '1px solid #e6c200', borderBottom: '1px solid #e6c200', padding: '8px 28px', fontSize: 13 }}>
        🧪 <b>TRAINING MODE</b> — Jarvis isn't contacting anyone outside. Emails go to <b>{(training.to || []).join(', ')}</b>; texts, calls and driver-app messages are held (a copy is emailed there).
      </div>
    )}
    <nav className="tmw-tabs" aria-label="Dispatch views" ref={tabsRef}>
      {[['jarvis', 'Ask Jarvis', ''], ...(canTeach ? [['teach', 'Teach Jarvis', '']] : []), ['watch', 'Priority', watchCounts && watchCounts.critical ? `${watchCounts.critical} critical` : ''], ['active', 'Active trips', String(trips.length)], ['map', 'Fleet map', ''], ['sheets', 'Trip sheets', ''], ['inbox', 'Jarvis inbox', inboxCount ? `${inboxCount} to file` : ''], ['calls', 'Calls & texts', ''], ['customers', 'Customers & brokers', ''], ['geofences', 'Geofences', ''], ['missing', 'Missing boxes', ''], ['roadcalls', 'Road calls', ''], ['drivers', 'Driver profiles', ''], ['employees', 'Employees', ''], ['outbound', 'Reports', ''], ['unclosed', 'Not closed', status && status.unclosed && status.unclosed.length ? `${status.unclosed.length} to close` : ''], ['delivered', 'Delivered', unackedCount ? `${unackedCount} to handle` : '']].map(([k, label, n]) => (
        <button key={k} type="button" className="tmw-tab" aria-pressed={tab === k} onClick={() => { playOpen(); setTab(k); }}>{label}{n ? <small>{n}</small> : null}</button>
      ))}
    </nav>

    {err && <div className="tmw-banner" data-kind="critical" role="alert">{err}</div>}

    {tab === 'watch' && (
      <div className="tmw-pane" style={{ padding: '20px 28px', maxWidth: '1100px' }}>
        <WatchtowerPanel onCounts={setWatchCounts} onOpenTrip={toTrip} trips={trips} onChanged={load} />
      </div>
    )}

    {texting && <Suspense fallback={null}><TextingSetup onClose={() => setTexting(false)} /></Suspense>}

    {tab === 'inbox' && (
      <div className="tmw-pane" style={{ padding: '20px 28px', maxWidth: '1100px' }}>
        <Suspense fallback={<div className="tmw-note">Loading the inbox…</div>}><JarvisInbox onCount={loadInboxCount} /></Suspense>
      </div>
    )}

    <Suspense fallback={null}><JarvisBubble /></Suspense>
    {people && <Suspense fallback={null}><DispatchersAdmin onClose={() => setPeople(false)} /></Suspense>}
    {companies && <Suspense fallback={null}><CompaniesAdmin onClose={() => setCompanies(false)} /></Suspense>}
    {pushes && <Suspense fallback={null}><PushRulesAdmin onClose={() => setPushes(false)} /></Suspense>}

    {tab === 'calls' && (
      <div className="tmw-pane" style={{ padding: '16px 20px', maxWidth: '1440px' }}>
        <Suspense fallback={<div className="tmw-note">Loading…</div>}><CallsTexts trips={trips} /></Suspense>
      </div>
    )}

    {tab === 'drivers' && (
      <div className="tmw-pane" style={{ padding: '20px 28px', maxWidth: '1300px' }}>
        <Suspense fallback={<div className="tmw-note">Loading…</div>}><DriverProfiles /></Suspense>
      </div>
    )}

    {tab === 'employees' && (
      <div className="tmw-pane" style={{ padding: '20px 28px', maxWidth: '1100px' }}>
        <Suspense fallback={<div className="tmw-note">Loading…</div>}><Employees /></Suspense>
      </div>
    )}

    {tab === 'roadcalls' && (
      <div className="tmw-pane" style={{ padding: '20px 28px', maxWidth: '1300px' }}>
        <Suspense fallback={<div className="tmw-note">Loading…</div>}><RoadCalls /></Suspense>
      </div>
    )}

    {tab === 'missing' && (
      <div className="tmw-pane" style={{ padding: '20px 28px', maxWidth: '1300px' }}>
        <Suspense fallback={<div className="tmw-note">Loading…</div>}><MissingBoxes /></Suspense>
      </div>
    )}

    {tab === 'customers' && (
      <div className="tmw-pane" style={{ padding: '20px 28px', maxWidth: '1300px' }}>
        <Suspense fallback={<div className="tmw-note">Loading…</div>}><CustomerProfiles key={customerQuery} initialQuery={customerQuery} /></Suspense>
      </div>
    )}

    {tab === 'geofences' && (
      <div className="tmw-pane" style={{ padding: '20px 28px', maxWidth: '1300px' }}>
        <Suspense fallback={<div className="tmw-note">Loading…</div>}><Geofences /></Suspense>
      </div>
    )}

    {tab === 'teach' && canTeach && (
      <div className="tmw-pane" style={{ padding: '20px 28px', maxWidth: '1100px' }}>
        <Suspense fallback={<div className="tmw-note">Loading…</div>}><TeachJarvis /></Suspense>
      </div>
    )}

    {tab === 'jarvis' && (
      <div className="tmw-pane" style={{ padding: '20px 28px', maxWidth: '1200px' }}>
        <Suspense fallback={<div className="tmw-note">Loading Jarvis…</div>}><JarvisChat /></Suspense>
      </div>
    )}

    {tab === 'unclosed' && (
      <div className="tmw-pane" style={{ padding: '20px 28px', maxWidth: '1100px' }}>
        <Suspense fallback={<div className="tmw-note">Loading…</div>}><NotClosed items={(status && status.unclosed) || []} onChanged={load} /></Suspense>
      </div>
    )}

    {tab === 'outbound' && (
      <div className="tmw-pane" style={{ padding: '20px 28px', maxWidth: '1100px' }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          {[['flowers', 'Flower loads update'], ['outbound', 'Morning outbound'], ['stops', 'Time at each stop']].map(([k, label]) => (
            <button key={k} type="button" className="tmw-btn tmw-btn-sm" aria-pressed={report === k} style={report === k ? { fontWeight: 800 } : undefined} onClick={() => setReport(k)}>{label}</button>
          ))}
        </div>
        <Suspense fallback={<div className="tmw-note">Loading the report…</div>}>{report === 'flowers' ? <FlowerReport /> : report === 'stops' ? <StopTimes /> : <OutboundReport />}</Suspense>
      </div>
    )}

    {tab === 'map' && (
      <div style={{ padding: '12px 20px 20px' }}>
        <Suspense fallback={<div className="tmw-note">Loading the map…</div>}><FleetMap trips={trips} onOpenTrip={toTrip} onOpenCustomer={(name) => { setCustomerQuery(name); setTab('customers'); }} /></Suspense>
      </div>
    )}

    {tab === 'sheets' && (
      <div className="tmw-pane" style={{ padding: '20px 28px', maxWidth: '1100px' }}>
        <TripSheetsPanel onOpenTrip={toTrip} />
      </div>
    )}

    {tab === 'active' && (
      <>
        {/* the search + filters, always shown; result buttons only once something is searched or filtered */}
        <div className="tmw-sp-wrap">
        <div className="tmw-toolbar tmw-sp">
          <label htmlFor="tmw-trip-search" className="tmw-sp-label">Search trips</label>
          <input id="tmw-trip-search" type="search" className="tmw-search" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Truck, trip, driver, bill or destination…" aria-controls="tmw-search-panel" />
          <TripSearchPanel id="tmw-search-panel" trips={filtered} runs={runs} runOn={runOn}
            active={!!query.trim() || runOn || statusFilter !== 'all' || billType !== 'all' || ocOnly || downOnly}
            runHub={runHub} setRunHub={setRunHub} runDay={runDay} setRunDay={setRunDay} runRoute={runRoute} setRunRoute={setRunRoute} routeOptions={routeOptions} recentDays={recentDays} dayLabel={runDayLabel}
            statusFilter={statusFilter} setStatusFilter={setStatusFilter} statusOptions={statusOptions} billType={billType} setBillType={setBillType} billTypeOptions={billTypeOptions} billLabel={billLabel}
            ocOnly={ocOnly} setOcOnly={setOcOnly} ocCount={trips.filter((t) => t.oc).length} downOnly={downOnly} setDownOnly={setDownOnly} downCount={trips.filter(isDown).length}
            selectedTrip={selectedTrip} onSelect={(n) => {
              setSelectedTrip(n);
              // bring that trip's card into view (on a phone it's far below the search)
              setTimeout(() => { const el = document.querySelector(`section.tmw-card[aria-label$="trip ${n}"]`); if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 50);
            }}
            onReset={() => { setRunHub('all'); setRunDay(''); setRunRoute('all'); setStatusFilter('all'); setBillType('all'); setOcOnly(false); setDownOnly(false); }} />
        </div>
        </div>
        {alertCount > 0 && <div className="tmw-banner">{alertCount} trip{alertCount === 1 ? '' : 's'} where Samsara disagrees with TruckMate (temperature or driver) — see Source conflicts in each trip.</div>}
        {loading && !status
          ? <div style={{ padding: '24px 28px' }}>Loading trips…</div>
          : <DispatchWorkspace trips={filtered} allTrips={trips} metaOf={(t) => statusMeta(t.statusCode, t.statusDesc)} renderMap={renderMap}
              selectedId={selectedTrip} onSelect={(n) => setSelectedTrip(n ? String(n) : null)} isOpen={isOpen} setOpen={setOpen}
              briefReq={briefReq} onChanged={load} onError={(m) => setErr(m)} onCounts={setWatchCounts} />}
      </>
    )}

    {tab === 'delivered' && (
      <div className="tmw-pane" style={{ padding: '20px 28px', maxWidth: '1100px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
          <div style={{ fontSize: '13px' }}>{unackedCount} awaiting handling{delivered.count ? ` · ${delivered.count} total` : ''}</div>
          <label style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
            <input type="checkbox" checked={showHandled} onChange={(e) => setShowHandled(e.target.checked)} />
            Show handled
          </label>
        </div>
        {visibleDelivered.length === 0 && <div className="tmw-empty">{showHandled ? 'Nothing in the delivered queue yet.' : 'No deliveries awaiting handling.'}</div>}
        {visibleDelivered.map((item) => (
          <DeliveredRow key={item.tripNumber} item={item} onAck={doAck} busy={ackBusy === String(item.tripNumber)}
            rundown={rundowns[String(item.tripNumber)]} onResend={async (trip) => { try { await sendRundown(trip); } catch (e) { setErr(e.message); } loadRundowns(); }}
            onReopen={async (trip) => { try { await reopenTrip(trip); load(); } catch (e) { setErr(e.message); } }} />
        ))}
      </div>
    )}
    </div>
  );
}
